import { roundDown, roundUp, toInteger } from './utils';
import { Node, XYPosition } from '@xyflow/react';

export type NodeBoundingBox = {
	id: string;
	width: number;
	height: number;
	topLeft: XYPosition;
	bottomLeft: XYPosition;
	topRight: XYPosition;
	bottomRight: XYPosition;
};

export type GraphBoundingBox = {
	width: number;
	height: number;
	topLeft: XYPosition;
	bottomLeft: XYPosition;
	topRight: XYPosition;
	bottomRight: XYPosition;
	xMax: number;
	yMax: number;
	xMin: number;
	yMin: number;
};

/**
 * Get the bounding box of all nodes and the graph itself, as X/Y coordinates
 * of all corner points.
 *
 * @param nodes The node list
 * @param nodePadding Optional padding to add to the node's and graph bounding boxes
 * @param roundTo Everything will be rounded to this nearest integer
 * @returns Graph and nodes bounding boxes.
 */
export const getBoundingBoxes = <NodeDataType extends Node = Node>(
	nodes: Node<NodeDataType>[],
	nodeXPadding = 2,
	nodeYPadding = 2,
	roundTo = 2
) => {
	let xMax = Number.MIN_SAFE_INTEGER;
	let yMax = Number.MIN_SAFE_INTEGER;
	let xMin = Number.MAX_SAFE_INTEGER;
	let yMin = Number.MAX_SAFE_INTEGER;

	const nodeBoxes: NodeBoundingBox[] = nodes.map((node) => {
		const width = Math.max(node.measured?.width || 250, 1);
		const height = Math.max(node.measured?.height || 100, 1);

		const position: XYPosition = {
			x: node.position?.x || 0,
			y: node.position?.y || 0,
		};

		const topLeft: XYPosition = {
			x: position.x - nodeXPadding,
			y: position.y - nodeYPadding,
		};
		const bottomLeft: XYPosition = {
			x: position.x - nodeXPadding,
			y: position.y + height + nodeYPadding,
		};
		const topRight: XYPosition = {
			x: position.x + width + nodeXPadding,
			y: position.y - nodeYPadding,
		};
		const bottomRight: XYPosition = {
			x: position.x + width + nodeXPadding,
			y: position.y + height + nodeYPadding,
		};

		if (roundTo > 0) {
			topLeft.x = roundDown(topLeft.x, roundTo);
			topLeft.y = roundDown(topLeft.y, roundTo);
			bottomLeft.x = roundDown(bottomLeft.x, roundTo);
			bottomLeft.y = roundUp(bottomLeft.y, roundTo);
			topRight.x = roundUp(topRight.x, roundTo);
			topRight.y = roundDown(topRight.y, roundTo);
			bottomRight.x = roundUp(bottomRight.x, roundTo);
			bottomRight.y = roundUp(bottomRight.y, roundTo);
		}

		if (topLeft.y < yMin) {
			yMin = topLeft.y;
		}
		if (topLeft.x < xMin) {
			xMin = topLeft.x;
		}
		if (bottomRight.y > yMax) {
			yMax = bottomRight.y;
		}
		if (bottomRight.x > xMax) {
			xMax = bottomRight.x;
		}

		return {
			id: node.id,
			width,
			height,
			topLeft,
			bottomLeft,
			topRight,
			bottomRight,
		};
	});

	const graphXPadding = toInteger(nodeXPadding, 0) * 2;
	const graphYPadding = toInteger(nodeXPadding, 0) * 2;

	xMax = roundUp(xMax + graphXPadding, roundTo);
	yMax = roundUp(yMax + graphYPadding, roundTo);
	xMin = roundDown(xMin - graphXPadding, roundTo);
	yMin = roundDown(yMin - graphYPadding, roundTo);

	const topLeft: XYPosition = {
		x: xMin,
		y: yMin,
	};

	const bottomLeft: XYPosition = {
		x: xMin,
		y: yMax,
	};

	const topRight: XYPosition = {
		x: xMax,
		y: yMin,
	};

	const bottomRight: XYPosition = {
		x: xMax,
		y: yMax,
	};

	const width = Math.abs(topLeft.x - topRight.x);
	const height = Math.abs(topLeft.y - bottomLeft.y);

	const graphBox: GraphBoundingBox = {
		topLeft,
		bottomLeft,
		topRight,
		bottomRight,
		width,
		height,
		xMax,
		yMax,
		xMin,
		yMin,
	};

	return { nodeBoxes, graphBox };
};
